// A sheet board's own actions in the command bar (Fixes 9): a link on the selected cell, and how its typed dates read
// (day first, as the UK writes them, or month first, as the US does). Each loads the sheet's code only when run.

import { ArrowDownUp, CalendarDays, ChartColumn, Combine, Eye, EyeOff, Funnel, Grid2x2, Link2, ListChecks, Paintbrush, Replace, Search, SquareCheck, StickyNote, Table2, Tag } from "lucide-react";
import { sheetDayFirst, sheetTabs, type SheetTab } from "../../../shared/sheet/model";
import { grownRect, mergeIndex } from "../../../shared/sheet/layout";
import type { Rect } from "../../../shared/sheet/addr";
import { boardEntries, get } from "../../state/store";
import { addCommands, type Command } from "./registry";
import { readStoredJson } from "../../lib/stored";

/** The open tab's selection as a block, taking in whole any merged cells it touches. */
function sheetRect(tab: SheetTab, st: typeof import("../../boards/sheet/state"), live?: import("../../boards/sheet/live").SheetLive): Rect {
  const sel = st.useSheetUi.getState().sel[tab.entry.id] ?? st.START;
  const x = st.selRect(sel);
  if (!sel.kind) return grownRect(mergeIndex(tab.data.merges), x);
  // whole columns or rows: as far as the cells in use
  const used = live?.book.extent(tab.entry.id) ?? { rows: x.r1 + 1, cols: x.c1 + 1 };
  return { r0: x.r0, c0: x.c0, r1: Math.max(x.r0, Math.min(x.r1, used.rows - 1)), c1: Math.max(x.c0, Math.min(x.c1, used.cols - 1)) };
}

addCommands("sheet", ({ board, kind }): Command[] => {
  if (!board || board.archived || kind !== "sheet" || get().guest) return [];
  const id = board.id;
  const tabs = () => sheetTabs(boardEntries(get(), id).filter((e) => e.t === "sheet"));
  const dayFirst = sheetDayFirst(tabs());
  // the selected cell of the tab open on this board
  const link = () =>
    void Promise.all([import("../../boards/sheet/state"), import("../../boards/sheet/link")]).then(([st, l]) => {
      const ui = st.useSheetUi.getState();
      const tab = tabs().find((t) => t.entry.id === ui.tab[id]) ?? tabs()[0];
      if (!tab) return;
      const sel = ui.sel[tab.entry.id] ?? st.START;
      l.openLinkBox(tab.entry.id, sel.r, sel.c);
    });
  // the open tab, and what is selected on it, for the sheet's own dialogs (Fixes 9)
  const open = (run: (st: typeof import("../../boards/sheet/state"), tab: ReturnType<typeof tabs>[number], range: string) => void) => () =>
    void Promise.all([import("../../boards/sheet/state"), import("../../../shared/sheet/addr")]).then(([st, addr]) => {
      const ui = st.useSheetUi.getState();
      const tab = tabs().find((t) => t.entry.id === ui.tab[id]) ?? tabs()[0];
      if (!tab) return;
      const sel = ui.sel[tab.entry.id] ?? st.START;
      run(st, tab, addr.rectName(st.selRect(sel)));
    });
  // the open tab worked out, with what is selected on it as a rule's range (Fixes 9: a whole column from under its header)
  const withLive = (run: (m: typeof import("../../boards/sheet/ops"), live: import("../../boards/sheet/live").SheetLive, tab: import("../../../shared/sheet/model").SheetTab, range: string, st: typeof import("../../boards/sheet/state")) => void) => () =>
    void Promise.all([import("../../boards/sheet/state"), import("../../boards/sheet/ops"), import("../../boards/sheet/live")]).then(([st, m, lv]) => {
      const live = lv.sheetLive(id, boardEntries(get(), id).filter((e) => e.t === "sheet" || e.t === "cells"));
      const ui = st.useSheetUi.getState();
      const tab = live.tabs.find((t) => t.entry.id === ui.tab[id]) ?? live.tabs[0];
      if (!tab) return;
      const sel = ui.sel[tab.entry.id] ?? st.START;
      run(m, live, tab, m.ruleRangeFor(live, tab, st.selRect(sel), sel.kind), st);
    });
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  // whether the open tab has a filter in this browser (Filter.tsx keeps them), for the row's words
  const filtered = (() => {
    const open = readStoredJson<{ tab?: Record<string, string> }>("cb:sheet", {}).tab?.[id] ?? tabs()[0]?.entry.id;
    return !!open && !!readStoredJson<Record<string, unknown>>("cb:sheet:filters", {})[open];
  })();
  return [
    { id: "sheet:find", group: "This board", label: "Find in this sheet", hint: "In this tab or every tab; it goes to each match however far down", kbd: mac ? "⌘ F" : "Ctrl F", icon: <Search size={15} />, keywords: "find search look for cell text value sheet tab", run: () => void import("../../boards/sheet/state").then((st) => st.openFind(id, false)) },
    { id: "sheet:replace", group: "This board", label: "Find and replace", hint: "Changes what was typed in every matching cell; Undo puts them back", kbd: mac ? "⌘ ⇧ H" : "Ctrl H", icon: <Replace size={15} />, keywords: "replace find change text every cell rename words", run: () => void import("../../boards/sheet/state").then((st) => st.openFind(id, true)) },
    { id: "sheet:sort", group: "This board", label: "Sort range", hint: "By one column, then by another for rows that tie", icon: <ArrowDownUp size={15} />, keywords: "sort order rows by column then by multiple columns ascending descending newest oldest a to z", run: open((st, tab) => st.openSheetDialog({ kind: "sort", board: id, tab: tab.entry.id })) },
    { id: "sheet:name", group: "This board", label: "Name a range", hint: "A word that stands for cells in formulas on any tab: =SUM(Rates)", icon: <Tag size={15} />, keywords: "named range name define range formulas names", run: open((st, tab, range) => st.openSheetDialog({ kind: "names", board: id, tab: tab.entry.id, range })) },
    { id: "sheet:names", group: "This board", label: "Open named ranges", hint: "Every name on this sheet and the cells it stands for", icon: <Tag size={15} />, keywords: "named ranges names list manage remove", run: open((st, tab) => st.openSheetDialog({ kind: "names", board: id, tab: tab.entry.id })) },
    { id: "sheet:dropdown", group: "This board", label: "Add a dropdown", hint: "On the selected cells: pick from a list of options, each a colored chip", icon: <ListChecks size={15} />, keywords: "dropdown drop down list pick list options data validation chips select choice", run: withLive((_m, _l, tab, range, st) => st.openSheetDialog({ kind: "dropdown", board: id, tab: tab.entry.id, range })) },
    { id: "sheet:checks", group: "This board", label: "Insert checkboxes", hint: "In the selected cells: a click or the space bar ticks one", icon: <SquareCheck size={15} />, keywords: "checkbox checkboxes tick box check done true false data validation", run: withLive((m, _l, tab, range) => m.addChecks(tab, range)) },
    { id: "sheet:colors", group: "This board", label: "Color cells by rule", hint: "A fill and text color for cells that are empty, hold some words, pass a number or a date before today", icon: <Paintbrush size={15} />, keywords: "conditional formatting color colour rule highlight fill overdue blank empty format by value", run: withLive((_m, _l, tab, range, st) => st.openSheetDialog({ kind: "colors", board: id, tab: tab.entry.id, range })) },
    {
      id: "sheet:filter",
      group: "This board",
      label: filtered ? "Remove filter" : "Create a filter",
      hint: filtered ? "Every row shows again; nothing in the sheet changed" : "On the header row: tick values or set a condition to hide rows from your view only",
      icon: <Funnel size={15} />,
      keywords: "filter rows hide show only values condition header funnel remove filter clear filter",
      run: withLive((_m, live, tab, _r, st) =>
        void import("../../boards/sheet/Filter").then((f) => {
          const ui = st.useSheetUi.getState();
          const sel = ui.sel[tab.entry.id] ?? st.START;
          if (f.useFilterUi.getState().filters[tab.entry.id]) f.setFilter(tab.entry.id, null);
          else f.setFilter(tab.entry.id, f.filterFor(live, tab, st.selRect(sel), sel.kind));
        }),
      ),
    },
    {
      id: "sheet:note",
      group: "This board",
      label: "Insert note",
      hint: "On the selected cell: words shown when you point at it (Shift F2 on a cell)",
      kbd: "Shift F2",
      icon: <StickyNote size={15} />,
      keywords: "note notes comment memo annotate cell edit note remove note",
      run: withLive((_m, _l, tab, _r, st) =>
        void import("../../boards/sheet/Note").then((nt) => {
          const sel = st.useSheetUi.getState().sel[tab.entry.id] ?? st.START;
          nt.openNote(tab.entry.id, sel.r, sel.c);
        }),
      ),
    },
    // a chart of the selection, beside it (Fixes 9, H2b)
    {
      id: "sheet:chart",
      group: "This board",
      label: "Insert chart",
      hint: "A column, line or pie chart of the selected cells, beside them; it follows them as they change",
      icon: <ChartColumn size={15} />,
      keywords: "chart graph plot column bar line pie visualize insert chart",
      run: withLive((m, live, tab, _r, st) => {
        const bad = m.insertChart(live, tab, sheetRect(tab, st, live));
        if (bad) void import("../../state/store").then((s) => s.toast({ level: "info", title: "Nothing to chart there", body: bad, key: "sheet-no" }));
      }),
    },
    // a table on the selection, or the filled block around the cell (Features 5)
    {
      id: "sheet:table",
      group: "This board",
      label: "Make a table",
      hint: "Its first row the headers: a formula typed in a column fills every row, and new rows under it join it",
      icon: <Table2 size={15} />,
      keywords: "table make table convert to table format as table calculated column structured reference header rows",
      run: withLive((m, live, tab, _r, st) => {
        const bad = m.makeTable(live, tab, sheetRect(tab, st, live));
        if (bad) void import("../../state/store").then((s) => s.toast({ level: "info", title: "That can't be a table", body: bad, key: "sheet-no" }));
      }),
    },
    // merged cells, borders and hidden rows and columns (Fixes 9, H2b), on what is selected
    {
      id: "sheet:merge",
      group: "This board",
      label: "Merge cells",
      hint: "The selected cells as one, its top left value across them; asks first if others hold something",
      icon: <Combine size={15} />,
      keywords: "merge cells combine join title across center span unmerge",
      run: withLive((_m, live, tab, _r, st) => void import("../../boards/sheet/Layout").then((l) => l.mergeOrAsk(live, tab, sheetRect(tab, st, live), "all"))),
    },
    { id: "sheet:unmerge", group: "This board", label: "Unmerge cells", hint: "The merged cells in the selection, back to one cell each", icon: <Combine size={15} />, keywords: "unmerge split cells separate merged", run: withLive((m, _l, tab, _r, st) => void m.unmergeCells(tab, sheetRect(tab, st))) },
    { id: "sheet:borders", group: "This board", label: "Add borders", hint: "A thin line round every selected cell; the toolbar's Borders has the rest", icon: <Grid2x2 size={15} />, keywords: "borders border lines outline grid box cells table", run: withLive((m, l, tab, _r, st) => void m.setBorders(tab, sheetRect(tab, st, l), "all", false)) },
    { id: "sheet:borders-off", group: "This board", label: "Clear borders", hint: "Takes the borders off the selected cells", icon: <Grid2x2 size={15} />, keywords: "clear borders remove borders no border lines", run: withLive((m, l, tab, _r, st) => void m.setBorders(tab, sheetRect(tab, st, l), "none", false)) },
    {
      id: "sheet:hide-cols",
      group: "This board",
      label: "Hide columns",
      hint: "The selected columns, out of view; their cells stay and formulas read them",
      icon: <EyeOff size={15} />,
      keywords: "hide column columns tuck away helper hidden",
      run: withLive((m, _l, tab, _r, st) => {
        const x = st.selRect(st.useSheetUi.getState().sel[tab.entry.id] ?? st.START);
        m.hideLines(tab, "col", x.c0, x.c1, true);
      }),
    },
    {
      id: "sheet:hide-rows",
      group: "This board",
      label: "Hide rows",
      hint: "The selected rows, out of view; their cells stay and formulas read them",
      icon: <EyeOff size={15} />,
      keywords: "hide row rows tuck away hidden",
      run: withLive((m, _l, tab, _r, st) => {
        const x = st.selRect(st.useSheetUi.getState().sel[tab.entry.id] ?? st.START);
        m.hideLines(tab, "row", x.r0, x.r1, true);
      }),
    },
    {
      id: "sheet:unhide",
      group: "This board",
      label: "Unhide all rows and columns",
      hint: "Every hidden row and column on this tab shows again",
      icon: <Eye size={15} />,
      keywords: "unhide show hidden rows columns reveal",
      run: withLive((m, _l, tab) => void m.putTab(tab, { hideRows: null, hideCols: null } as never, "Unhide all")),
    },
    { id: "sheet:link", group: "This board", label: "Insert link", hint: "On the selected cell: its text opens a web or mail address (Ctrl K on a cell)", icon: <Link2 size={15} />, keywords: "link hyperlink url address web mail edit link remove link cell", run: link },
    {
      id: "sheet:dates",
      group: "This board",
      label: dayFirst ? "Read typed dates month first" : "Read typed dates day first",
      hint: dayFirst ? "03/04/2026 becomes March 4 on this sheet" : "03/04/2026 becomes 3 April on this sheet",
      icon: <CalendarDays size={15} />,
      keywords: "dates date order day first month first uk us american british region locale typed dates format",
      run: () => void import("../../boards/sheet/ops").then((m) => m.setDayOrder(tabs(), !dayFirst)),
    },
  ];
});
